import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as auth from "../services/auth";
import {
  registerReauthenticationHandler,
  registerSessionExpiredHandler,
} from "../services/sessionRuntime";
import ReauthenticationModal from "../components/ReauthenticationModal";

const AuthCtx = createContext(null);

const withRole = (user) =>
  user ? { ...user, role: String(user.role || "MEMBER").toUpperCase() } : null;

const isValidMembershipSubscription = (subscription) => {
  if (!subscription) return false;

  if (subscription.status === "active") {
    if (!subscription.current_period_end && !subscription.expires_at) return true;

    const activeUntil =
      subscription.current_period_end || subscription.expires_at;
    return activeUntil ? new Date(activeUntil) > new Date() : true;
  }

  if (subscription.status === "trialing") {
    return subscription.trial_end
      ? new Date(subscription.trial_end) > new Date()
      : false;
  }

  return false;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [coreProgress, setCoreProgress] = useState([]);

  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthLoading, setReauthLoading] = useState(false);
  const [reauthError, setReauthError] = useState("");
  const reauthPromiseRef = useRef(null);
  const lastInteractionRef = useRef(Date.now());
  const lastTouchRef = useRef(0);
  const lastFocusSyncRef = useRef(0);

  const clearUserState = useCallback(() => {
    setUser(null);
    setSubscriptions([]);
    setCoreProgress([]);
  }, []);

  const applyFreshSession = useCallback((fresh) => {
    if (!fresh?.user) return null;

    const normalizedUser = withRole(fresh.user);
    setUser(normalizedUser);
    setSubscriptions(fresh.subscriptions || []);
    setCoreProgress(fresh.core_progress || []);
    return normalizedUser;
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const fresh = await auth.me();
      return applyFreshSession(fresh);
    } catch (err) {
      if (err?.status === 401) {
        auth.clearLocalSession();
        clearUserState();
      } else {
        console.error("[AUTH_REFRESH_ERROR]", err);
      }
      return null;
    }
  }, [applyFreshSession, clearUserState]);

  useEffect(() => {
    let active = true;

    const init = async () => {
      try {
        const restored = await auth.restoreSession();

        if (active && restored) {
          const fresh = await auth.me();
          if (active) applyFreshSession(fresh);
        }
      } catch (err) {
        console.error("[AUTH_INIT_ERROR]", err);
        auth.clearLocalSession();
        if (active) clearUserState();
      } finally {
        if (active) setInitialized(true);
      }
    };

    init();

    return () => {
      active = false;
    };
  }, [applyFreshSession, clearUserState]);

  const login = useCallback(
    async (email, password) => {
      setLoading(true);

      try {
        const data = await auth.login(email, password);
        setUser(withRole(data?.user));

        const fresh = await auth.me();
        applyFreshSession(fresh);

        return data?.user;
      } finally {
        setLoading(false);
      }
    },
    [applyFreshSession]
  );

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      clearUserState();
    }
  }, [clearUserState]);

  const updateProfileAndCompleteOnboarding = useCallback(
    async (payload) => {
      setLoading(true);

      try {
        const result = await auth.updateProfile(payload);
        const updatedUser = withRole(result?.user);

        if (updatedUser) setUser(updatedUser);

        const fresh = await auth.me();
        return applyFreshSession(fresh) || updatedUser;
      } finally {
        setLoading(false);
      }
    },
    [applyFreshSession]
  );

  useEffect(() => {
    registerSessionExpiredHandler((reason) => {
      auth.clearLocalSession();
      clearUserState();

      try {
        sessionStorage.setItem(
          "zedro_session_notice",
          reason === "session_idle_expired"
            ? "Tu sesión se cerró por inactividad. Ingresa nuevamente."
            : "Por seguridad, inicia sesión nuevamente."
        );
      } catch {}
    });

    return () => registerSessionExpiredHandler(null);
  }, [clearUserState]);

  useEffect(() => {
    registerReauthenticationHandler(
      () =>
        new Promise((resolve) => {
          if (reauthPromiseRef.current) {
            reauthPromiseRef.current(false);
          }

          reauthPromiseRef.current = resolve;
          setReauthError("");
          setReauthOpen(true);
        })
    );

    return () => registerReauthenticationHandler(null);
  }, []);

  const confirmReauthentication = useCallback(async (password) => {
    setReauthLoading(true);
    setReauthError("");

    try {
      await auth.reauthenticate(password);
      setReauthOpen(false);
      reauthPromiseRef.current?.(true);
      reauthPromiseRef.current = null;
    } catch (err) {
      setReauthError(err?.message || "No pudimos confirmar tu identidad.");
    } finally {
      setReauthLoading(false);
    }
  }, []);

  const cancelReauthentication = useCallback(() => {
    if (reauthLoading) return;
    setReauthOpen(false);
    setReauthError("");
    reauthPromiseRef.current?.(false);
    reauthPromiseRef.current = null;
  }, [reauthLoading]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "zedro::logout") {
        auth.clearLocalSession();
        clearUserState();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clearUserState]);

  useEffect(() => {
    if (!user) return undefined;

    const recordInteraction = () => {
      lastInteractionRef.current = Date.now();
    };

    const interactionEvents = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
    ];

    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, recordInteraction, { passive: true })
    );

    const role = String(user.role || "MEMBER").toUpperCase();
    const isPrivileged = role === "ADMIN" || role === "STAFF";
    const checkEveryMs = isPrivileged ? 2 * 60 * 1000 : 15 * 60 * 1000;

    const intervalId = window.setInterval(async () => {
      const hadActivity = lastInteractionRef.current > lastTouchRef.current;
      if (!hadActivity || document.visibilityState !== "visible") return;

      const result = await auth.touchSession().catch(() => ({ ok: false }));
      if (result?.ok) lastTouchRef.current = Date.now();
    }, checkEveryMs);

    const syncWhenVisible = async () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastFocusSyncRef.current < 60 * 1000) return;

      lastFocusSyncRef.current = Date.now();
      await refreshSession();
    };

    window.addEventListener("focus", syncWhenVisible);
    window.addEventListener("pageshow", syncWhenVisible);
    document.addEventListener("visibilitychange", syncWhenVisible);

    return () => {
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, recordInteraction)
      );
      window.clearInterval(intervalId);
      window.removeEventListener("focus", syncWhenVisible);
      window.removeEventListener("pageshow", syncWhenVisible);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [user, refreshSession]);

  const hasActiveSubscription =
    Array.isArray(subscriptions) &&
    subscriptions.some(isValidMembershipSubscription);

  if (!initialized) return null;

  return (
    <AuthCtx.Provider
      value={{
        user,
        subscriptions,
        hasActiveSubscription,
        coreProgress,
        loading,
        initialized,
        refreshSession,
        login,
        logout,
        updateProfileAndCompleteOnboarding,
      }}
    >
      {children}

      <ReauthenticationModal
        open={reauthOpen}
        loading={reauthLoading}
        error={reauthError}
        onConfirm={confirmReauthentication}
        onCancel={cancelReauthentication}
      />
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
